// The Machines Map as pure data (#6562, epic #6552, prototype `mapView`): one square per
// machine, coloured by Health, CPU, Memory or Disk, its category, its glyph and the legend
// with its counts. `MachineMap` (the presenter) only renders what this returns.
//
// EVERY JUDGEMENT IS THE SERVER'S, as in `fleet-machines.ts`: a square reads the machine's
// `verdict` (#6557), its status, its health and the tier of each signal. Nothing here
// re-grades a raw reading, and the legend's thresholds are the domain's own constants.
//
// THE CATEGORY, first match wins:
//   off      not responding or offline: the outlined red square with "×" (U+00D7)
//   crit     a working or idle machine whose health is critical: red fill, "!"
//   warn     a working or idle machine whose health is a warning: amber; also a paused,
//            starting or waiting machine with any signal at warn or crit (see below)
//   unknown  presence unreadable: the outlined neutral square, no glyph, never the "×"
//   starting waiting to connect, or inside its first-beat grace: outlined, dotted border
//   paused   drained: dashed border
//   idle     the track
//   working  neutral strong
//
// THE NEUTRAL STATUSES ARE READ OFF THEIR SIGNALS. `machineVerdict` gives a paused,
// starting or waiting machine NEUTRAL health whatever its signals say (owner note 4 on
// #6557), so its health alone can never colour it. The issue rules a paused machine with
// low disk amber, as in the prototype; on the Health map a critical signal there is amber
// too, never red, because a machine that is not running work by design never needs
// attention. A reading mode still draws the reading itself (a critical disk is red there).
//
// THE COLOUR MODES (CPU / Memory / Disk) draw the one reading: a neutral ramp in four
// steps whose strength follows the value, amber at warn, red with "!" at crit. A silent
// machine keeps its "×" in every mode. A reading the machine did not send, or a CALM disk
// graded by its floor alone (a runner older than #6554: no total, so no ratio), is the
// outlined square: there is nothing to draw a strength from, and it is never a
// zero-strength ramp. A floor-only disk UNDER its floor is still critical: red, with "!".
//
// THE RAMP IS QUANTIZED HERE, NOT MIXED AT RUNTIME. The app's styles take no runtime
// `color-mix` (`styles/README.md`), so a square carries its step (1 to 4) and the stylesheet
// draws each step at a fixed strength.

import {
  MACHINE_CPU_BUSY_RATIO,
  MACHINE_CPU_OVERLOAD_RATIO,
  MACHINE_DISK_CRIT_RATIO,
  MACHINE_DISK_FLOOR_MARGIN_BYTES,
  MACHINE_DISK_WARN_RATIO,
  MACHINE_MEMORY_CRIT_RATIO,
  MACHINE_MEMORY_WARN_RATIO,
  MACHINE_NEUTRAL_STATUSES,
  MACHINE_OVERLOAD_WINDOW_SAMPLES,
  type MachineHealth,
  type MachineSignal,
  type MachineStatus,
} from '@developerz/domain';
import { OFFLINE_STATUSES, shortId } from './fleet-machines';
import type { UtilBox } from './fleetUtilization';
import { resourceBar } from './machine-bars';
import { MACHINE_HEALTH, MACHINE_STATUS, serverText } from './machine-labels';
import { decimal } from './time';

/** The "Show" switch, in the prototype's order. */
export const MAP_MODES = ['health', 'cpu', 'memory', 'disk'] as const;
export type MapMode = (typeof MAP_MODES)[number];

/** Every category, in the legend's order (the prototype's `CAT_ORDER`, then the two it lacks). */
export const MAP_CATEGORIES = [
  'crit',
  'off',
  'warn',
  'working',
  'idle',
  'paused',
  'starting',
  'unknown',
] as const;
export type MapCategory = (typeof MAP_CATEGORIES)[number];

/** Shown in the Health legend at zero too, as the prototype does; the rest only when present. */
const ALWAYS_LISTED: readonly MapCategory[] = ['crit', 'off', 'warn', 'working', 'idle', 'paused'];

/** How a square is drawn: a category (Health), or a reading (the other modes). */
export type SquareLook = MapCategory | 'ramp' | 'not_reported';
export type MapGlyph = '!' | '×' | '';
export type RampStep = 1 | 2 | 3 | 4;
export type MapSquare = {
  readonly look: SquareLook;
  readonly glyph: MapGlyph;
  /** The ramp's strength, 1 (low) to 4 (high), for a `ramp` square only. */
  readonly step: RampStep | null;
};

/** The squares are large at or under this many machines in the whole fleet (the prototype's rule). */
export const MAP_LARGE_AT = 60;

const CROSS: MapGlyph = '×';
const NEUTRAL: readonly MachineStatus[] = MACHINE_NEUTRAL_STATUSES;

const signalsOf = (b: UtilBox): readonly MachineSignal[] => [
  b.verdict.signals.cpu,
  b.verdict.signals.memory,
  b.verdict.signals.disk,
];

const raised = (s: MachineSignal): boolean => s.level === 'warn' || s.level === 'crit';

/** One machine's category on the Health map. */
export function mapCategory(b: UtilBox): MapCategory {
  const { status, health } = b.verdict;
  if (OFFLINE_STATUSES.includes(status)) return 'off';
  if (status === 'working' || status === 'idle') {
    const tone = MACHINE_HEALTH[health].tone;
    if (tone === 'crit') return 'crit';
    if (tone === 'warn') return 'warn';
    return status;
  }
  if (NEUTRAL.includes(status) && signalsOf(b).some(raised)) return 'warn';
  if (status === 'unknown') return 'unknown';
  if (status === 'paused') return 'paused';
  return 'starting';
}

const GLYPH: Readonly<Partial<Record<SquareLook, MapGlyph>>> = { crit: '!', off: CROSS };

const square = (look: SquareLook, step: RampStep | null = null): MapSquare => ({
  look,
  glyph: GLYPH[look] ?? '',
  step,
});

const RAMP: readonly RampStep[] = [1, 2, 3, 4];

/**
 * A calm reading as a ramp step: one per fifth of 0 to 100%, the prototype's linear
 * strength. Only four are ever drawn: a ramp square is a reading at `ok`, under its warning
 * line, and every warning line is at or under 80% (`MACHINE_*_WARN_RATIO`,
 * `MACHINE_CPU_BUSY_RATIO`), so the top fifth is always amber or red instead.
 */
export function rampStep(ratio: number): RampStep {
  const fifth = Math.floor(Math.min(Math.max(ratio, 0), 1) * 5);
  return RAMP[Math.min(fifth, RAMP.length - 1)] ?? 4;
}

const SIGNAL: Readonly<Record<Exclude<MapMode, 'health'>, 'cpu' | 'memory' | 'disk'>> = {
  cpu: 'cpu',
  memory: 'memory',
  disk: 'disk',
};

/** How one machine's square is drawn in one mode. */
export function mapSquare(b: UtilBox, mode: MapMode): MapSquare {
  const category = mapCategory(b);
  if (mode === 'health' || category === 'off') return square(category);
  const signal = b.verdict.signals[SIGNAL[mode]];
  if (signal.level === 'crit') return square('crit');
  if (signal.level === 'warn') return square('warn');
  if (signal.level === 'not_reported' || signal.ratio === null) return square('not_reported');
  return square('ramp', rampStep(signal.ratio));
}

/** One legend line: the swatch, its words and how many machines it stands for. */
export type LegendEntry = {
  readonly look: SquareLook;
  readonly glyph: MapGlyph;
  readonly label: string;
  readonly count: number;
};

type Tr = (key: string, vars?: Readonly<Record<string, string | number>>) => string;

const pct = (ratio: number): number => Math.round(ratio * 100);

const CATEGORY_LABEL: Readonly<Record<MapCategory, string>> = {
  crit: 'fleet.machines.map_cat_crit',
  off: 'fleet.machines.map_cat_off',
  warn: 'fleet.machines.map_cat_warn',
  working: 'fleet.machines.status_working',
  idle: 'fleet.machines.status_idle',
  paused: 'fleet.machines.status_paused',
  starting: 'fleet.machines.status_starting',
  unknown: 'fleet.machines.map_cat_unknown',
};

const GB = 1e9;

/**
 * The disk legend's critical line. Critical is under each machine's OWN floor plus the
 * margin, so when every reporting machine shares one floor the line names that number;
 * when floors differ (or none reported one) it names the rule instead of one machine's.
 */
function diskCritLabel(boxes: readonly UtilBox[], tr: Tr): string {
  const over = pct(MACHINE_DISK_CRIT_RATIO);
  const floors = new Set<number>();
  for (const b of boxes) {
    const floor = b.stats?.workspaceDisk?.floorBytes;
    if (floor !== undefined) floors.add(floor);
  }
  const [only] = floors;
  if (floors.size === 1 && only !== undefined) {
    return tr('fleet.machines.map_disk_crit', {
      over,
      free: decimal((only + MACHINE_DISK_FLOOR_MARGIN_BYTES) / GB),
    });
  }
  return tr('fleet.machines.map_disk_crit_floor', {
    over,
    margin: decimal(MACHINE_DISK_FLOOR_MARGIN_BYTES / GB),
  });
}

function thresholdLabels(
  boxes: readonly UtilBox[],
  mode: Exclude<MapMode, 'health'>,
  tr: Tr,
): { readonly warn: string; readonly crit: string } {
  switch (mode) {
    case 'cpu':
      return {
        warn: tr('fleet.machines.map_cpu_warn', { busy: pct(MACHINE_CPU_BUSY_RATIO) }),
        crit: tr('fleet.machines.map_cpu_crit', {
          over: pct(MACHINE_CPU_OVERLOAD_RATIO),
          minutes: MACHINE_OVERLOAD_WINDOW_SAMPLES,
        }),
      };
    case 'memory':
      return {
        warn: tr('fleet.machines.map_warn_range', {
          low: pct(MACHINE_MEMORY_WARN_RATIO),
          high: pct(MACHINE_MEMORY_CRIT_RATIO),
        }),
        crit: tr('fleet.machines.map_crit_over', { over: pct(MACHINE_MEMORY_CRIT_RATIO) }),
      };
    case 'disk':
      return {
        warn: tr('fleet.machines.map_warn_range', {
          low: pct(MACHINE_DISK_WARN_RATIO),
          high: pct(MACHINE_DISK_CRIT_RATIO),
        }),
        crit: diskCritLabel(boxes, tr),
      };
  }
}

/**
 * The legend for one mode, every count off the whole fleet (the prototype's `all`), so the
 * counts sum to the number of machines. Health lists its six categories always and
 * `starting` / `unknown` when a machine is in one; a reading mode lists the ramp, warn,
 * crit and silent always, and "Not reported" when a machine sent no reading.
 */
export function mapLegend(boxes: readonly UtilBox[], mode: MapMode, tr: Tr): LegendEntry[] {
  const counts = new Map<SquareLook, number>();
  for (const b of boxes) {
    const look = mapSquare(b, mode).look;
    counts.set(look, (counts.get(look) ?? 0) + 1);
  }
  const entry = (look: SquareLook, label: string): LegendEntry => ({
    look,
    glyph: GLYPH[look] ?? '',
    label,
    count: counts.get(look) ?? 0,
  });
  if (mode === 'health') {
    return MAP_CATEGORIES.filter((c) => ALWAYS_LISTED.includes(c) || counts.has(c)).map((c) =>
      entry(c, tr(CATEGORY_LABEL[c])),
    );
  }
  const th = thresholdLabels(boxes, mode, tr);
  const out = [
    entry('ramp', tr('fleet.machines.map_ramp')),
    entry('warn', th.warn),
    entry('crit', th.crit),
    entry('off', tr('fleet.machines.map_cat_off')),
  ];
  return counts.has('not_reported')
    ? [...out, entry('not_reported', tr('fleet.machines.map_not_reported'))]
    : out;
}

/** The ramp's steps the legend shows, low to high (the prototype's four swatches). */
export const LEGEND_RAMP: readonly RampStep[] = RAMP;

/**
 * What `machineHealth` (`@developerz/domain`) would call a machine's signals had its status
 * not neutralised them: the same fold, worst first (critical CPU, disk, memory, then warning
 * disk, memory, CPU), or null when every signal is calm.
 */
function signalHealth(s: UtilBox['verdict']['signals']): MachineHealth | null {
  if (s.cpu.level === 'crit') return 'overloaded';
  if (s.disk.level === 'crit') return 'disk_critical';
  if (s.memory.level === 'crit') return 'memory_critical';
  if (s.disk.level === 'warn') return 'disk_warning';
  if (s.memory.level === 'warn') return 'memory_warning';
  if (s.cpu.level === 'warn') return 'busy';
  return null;
}

/**
 * The health WORD a square is named by. A working, idle or silent machine says its verdict's
 * health, as the card's pill does. A neutral status with a raised signal (the amber paused
 * square) names its worst signal ({@link signalHealth}), so an amber square is never
 * announced "Healthy".
 */
export function mapHealthKey(b: UtilBox): string {
  const raisedHealth = NEUTRAL.includes(b.verdict.status) ? signalHealth(b.verdict.signals) : null;
  return MACHINE_HEALTH[raisedHealth ?? b.verdict.health].key;
}

/** What a square says: its accessible name and its tooltip's three lines. */
export type SquareText = {
  /** Name, health, status and CPU: the square's accessible name. */
  readonly name: string;
  readonly title: string;
  /** `<8-char id> · <server>`. */
  readonly where: string;
  /** `<health> · <status>`. */
  readonly state: string;
  /** `CPU <v> · Memory <v> · Disk <v>`. */
  readonly readings: string;
};

/** One square's words, through the caller's translator. */
export function squareText(b: UtilBox, tr: Tr): SquareText {
  const health = tr(mapHealthKey(b));
  const status = tr(MACHINE_STATUS[b.verdict.status]);
  const cpu = resourceBar(b, 'cpu', tr);
  const bars = [cpu, resourceBar(b, 'memory', tr), resourceBar(b, 'disk', tr)];
  const server = serverText(b);
  return {
    name: tr('fleet.machines.map_square_aria', {
      name: b.display_name,
      health,
      status,
      cpu_label: cpu.label,
      cpu: cpu.value,
    }),
    title: b.display_name,
    where: server === null ? shortId(b.id) : `${shortId(b.id)} · ${server}`,
    state: `${health} · ${status}`,
    readings: bars.map((bar) => `${bar.label} ${bar.value}`).join(' · '),
  };
}
